// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — every colour that carries meaning, in one place so tests/contrast.test.ts can hold all
// of them to WCAG 2.1 §1.4.11's 3:1 floor for a non-text graphic.
//
// Two rules shaped this list and both are deliberate:
//
// 1. COLOUR IS NEVER THE ONLY CHANNEL. Each suit also has a glyph (cards.ts) and every card face
//    carries its rank as a numeral. A player with no colour vision at all loses nothing but flavour,
//    which matters more here than in most games: the entire mechanic is identifying somebody else's
//    card at a glance across a table.
// 2. EVERY SUIT FILL IS LIGHT. Card ink is one dark navy on all seven fills rather than a per-suit
//    ink, so there is one contrast pair to prove per suit instead of two, and no combination can be
//    quietly introduced that nobody measured. It also means the fills are all far from the board,
//    which is the pair that actually decides whether you can find a card on a phone.

export const INK = '#0b1020';
/** The page behind everything. */
export const SKY = '#0b1020';
/** The table the cards sit on, and the surface most things are measured against. */
export const BOARD = '#161d33';
/** The raised panels: a hand, the plume rack, the results card. */
export const PANEL = '#1e2740';
export const TEXT = '#e8ecf7';
export const DIM = '#9aa6c4';
export const SPARK = '#ffd76a';
export const HAZE = '#ff7b6b';
export const GOOD = '#6ee7a8';
/** The ring around whoever is to act. Must be findable at a glance on a phone. */
export const ACTIVE = '#ffd76a';

/** Suit fills, indexed by suit id. Index 5 is the sixth suit, which mode decides the identity of. */
export const SUIT_FILL: readonly string[] = [
  '#f2a93b', // Amber
  '#4cc9e0', // Cyan
  '#ff8ab0', // Rose
  '#6ed37c', // Verdant
  '#b79cff', // Violet
];

/** Prism is deliberately near-white: it is the suit that answers to every colour, so it is no colour. */
export const PRISM_FILL = '#eef0fb';
/** Onyx is a polished pale slate, not black — a black card on a night sky is a card nobody can find. */
export const ONYX_FILL = '#c3ccdf';

export function fillFor(suitId: number, sixth: 'prism' | 'onyx' | null): string {
  if (suitId < 5) return SUIT_FILL[suitId];
  return sixth === 'onyx' ? ONYX_FILL : PRISM_FILL;
}

/**
 * An unraised plume slot, and the back of a card whose face you are not allowed to see.
 *
 * All three of these started two shades darker, because a dark, recessive empty slot is what a
 * moody night sky wants. Measured, they were 1.39:1, 1.58:1 and 2.91:1 — drawn, correctly placed,
 * and invisible, which is exactly the failure principle #22 exists for. An unraised plume that
 * cannot be told from the board is not atmosphere, it is a player who cannot see how far the crew
 * has got. They are all lighter than they "should" be on purpose.
 */
export const SLOT = '#7a89b8';
export const CARD_BACK = '#6b7ab5';
/** The edge that makes a card back readable as a card rather than a hole in the panel. */
export const CARD_BACK_EDGE = '#e8eefb';

/**
 * The pairs tests/contrast.test.ts holds at >= 3:1. Written out longhand rather than generated, so a
 * suit added without a measurement is a failing build rather than an invisible card.
 */
export const CONTRAST_PAIRS: ReadonlyArray<[string, string, string]> = [
  ['Amber card on the board', SUIT_FILL[0], BOARD],
  ['Cyan card on the board', SUIT_FILL[1], BOARD],
  ['Rose card on the board', SUIT_FILL[2], BOARD],
  ['Verdant card on the board', SUIT_FILL[3], BOARD],
  ['Violet card on the board', SUIT_FILL[4], BOARD],
  ['Prism card on the board', PRISM_FILL, BOARD],
  ['Onyx card on the board', ONYX_FILL, BOARD],
  ['Amber card on a panel', SUIT_FILL[0], PANEL],
  ['Cyan card on a panel', SUIT_FILL[1], PANEL],
  ['Rose card on a panel', SUIT_FILL[2], PANEL],
  ['Verdant card on a panel', SUIT_FILL[3], PANEL],
  ['Violet card on a panel', SUIT_FILL[4], PANEL],
  ['Prism card on a panel', PRISM_FILL, PANEL],
  ['Onyx card on a panel', ONYX_FILL, PANEL],
  ['Card ink on Amber', INK, SUIT_FILL[0]],
  ['Card ink on Cyan', INK, SUIT_FILL[1]],
  ['Card ink on Rose', INK, SUIT_FILL[2]],
  ['Card ink on Verdant', INK, SUIT_FILL[3]],
  ['Card ink on Violet', INK, SUIT_FILL[4]],
  ['Card ink on Prism', INK, PRISM_FILL],
  ['Card ink on Onyx', INK, ONYX_FILL],
  ['Empty plume slot on the board', SLOT, BOARD],
  ['A hidden card back on a panel', CARD_BACK, PANEL],
  ['The edge of a hidden card', CARD_BACK_EDGE, CARD_BACK],
  ['Spark counter on a panel', SPARK, PANEL],
  ['Haze counter on a panel', HAZE, PANEL],
  ['Active-seat ring on a panel', ACTIVE, PANEL],
  ['Body text on the board', TEXT, BOARD],
  ['Body text on a panel', TEXT, PANEL],
  ['Dimmed text on a panel', DIM, PANEL],
  ['Dimmed text on the board', DIM, BOARD],
  ['Good/complete marker on a panel', GOOD, PANEL],
];
